import * as React from 'react';
import { mergeEventHandlers } from './ReactHelpers';

export interface PassiveAggressiveTimerProps {
  /**
   * The element to wrap with AutoDismiss functionality.
   * If you have a case where this typedef doesn't work for you, please reach out in #tab-dev-shared-widgets.
   */
  children: React.ReactElement;
  /** The amount of time (in ms) that must elapse before `handleTimerElapsed` will be called */
  timerDuration: number;
  /** The handler to be called after `dismissTimerDuration` has elapsed */
  handleTimerElapsed: () => void;
}

/**
 * A component used to trigger a handler when time-interval has elapsed.
 * The time-interval is reset if:
 *  • the document loses focus
 *  • the element recieves mouse-interactivity
 */
export class PassiveAggressiveTimer extends React.Component<PassiveAggressiveTimerProps> {
  private timerId: number | null = null;
  private boundStopTimer: (() => void) | null = null;
  private boundStartTimer: (() => void) | null = null;

  public componentDidMount(): void {
    this.boundStartTimer = this.startTimer.bind(this);
    this.boundStopTimer = this.stopTimer.bind(this);
    window.addEventListener('blur', this.boundStopTimer!);
    window.addEventListener('focus', this.boundStartTimer!);

    this.startTimer();
  }

  public componentWillUnmount(): void {
    window.removeEventListener('blur', this.boundStopTimer!);
    window.removeEventListener('focus', this.boundStartTimer!);

    this.stopTimer();
  }

  private handleMouseOver: React.MouseEventHandler<HTMLElement> = mergeEventHandlers(e => {
    this.stopTimer();
  }, e => this.props.children?.props.onMouseOver?.(e));

  private handleMouseOut: React.MouseEventHandler<HTMLElement> = mergeEventHandlers(e => {
    this.startTimer();
  }, e => this.props.children?.props.onMouseOut?.(e));

  private handleTouchStart: React.TouchEventHandler<HTMLElement> = mergeEventHandlers(e => {
    this.stopTimer();
  }, e => this.props.children?.props.onTouchStart?.(e));

  private handleTouchEnd: React.TouchEventHandler<HTMLElement> = mergeEventHandlers(e => {
    this.startTimer();
  }, e => this.props.children?.props.onTouchEnd?.(e));

  private stopTimer = (): void => {
    if (this.timerId) {
      window.clearTimeout(this.timerId);
    }
  }

  private startTimer = (): void => {
    this.stopTimer();

    if (this.props.handleTimerElapsed) {
      this.timerId = window.setTimeout(this.props.handleTimerElapsed, this.props.timerDuration);
    }
  }

  public render(): JSX.Element {
    const child: React.ReactElement = React.Children.only(this.props.children);

    return React.cloneElement(child, {
      onMouseOut: this.handleMouseOut,
      onMouseOver: this.handleMouseOver,
      onTouchEnd: this.handleTouchEnd,
      onTouchStart: this.handleTouchStart,
    });
  }
}
